import { useTranslation } from 'react-i18next'
import { Box, Flex, Text } from '@chakra-ui/react'
import { PickupStatus } from '@leid/api-orders'

import { OrderInfo } from 'appTypes/orders'
import {
  sortLocationOrderItemsGroupsFirstToPickup,
  sortOrderItemsFromFirstToPick,
  sortOrderItemsFromFirstToReturn,
  sortOrderItemsFromLatest,
} from 'components/partials/rentalsHistory/helpers'
import {
  combineDividedComplementaryItems,
  preProcessBundleItems,
} from 'components/partials/rentalsHistory/RentalsHistorySection/helpers'
import groupBy from 'lodash/groupBy'
import { getReductionDescriptionText } from 'utils/priceDescription'
import { removeDecimals } from 'utils/removeDecimals'

import IndividualOrderItemInfo from '../IndividualOrderItemInfo/IndividualOrderItemInfo'

import ContinuePaymentButtons from './components/ContinuePaymentButtons'
import { PayForReconciliationsButtons } from './components/PayForReconciliationsButtons'
import { JSX } from 'react'

type Props = {
  orderIsLoading: boolean
  info: OrderInfo
  refetch: () => Promise<void>
  isPublicPage: boolean
}

const IndividualOrderInfo = ({ orderIsLoading, info, refetch, isPublicPage }: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'cart', 'statuses'])

  let orderItems = combineDividedComplementaryItems(info.orderItems)
  orderItems = preProcessBundleItems(orderItems)

  const locationGroups = groupBy(orderItems, (value) => value.orderItem.locationId)

  const sortedLocationGroups = Object.values(locationGroups).map((orderItems) => {
    let notPickedUpItems = orderItems.filter((s) => s.orderItem.pickupStatus === PickupStatus.EMPTY)
    let pickedUpItems = orderItems.filter((s) => s.orderItem.pickupStatus === PickupStatus.PICKEDUP)
    let returnedItems = orderItems.filter((s) =>
      [PickupStatus.RETURNED, PickupStatus.INSPECTED, PickupStatus.COMPLETED].includes(s.orderItem.pickupStatus),
    )

    // Not picked up - order by first to pick up FIRST
    notPickedUpItems = sortOrderItemsFromFirstToPick(notPickedUpItems)
    pickedUpItems = sortOrderItemsFromFirstToReturn(pickedUpItems)
    returnedItems = sortOrderItemsFromLatest(returnedItems)

    return [...notPickedUpItems, ...pickedUpItems, ...returnedItems]
  })

  orderItems = sortLocationOrderItemsGroupsFirstToPickup(sortedLocationGroups).flat()

  const getTotalPrice = () => {
    const total = info.order.totalPrice + (info.order.deposit ?? 0)
    return total
  }

  const getOriginalPrice = () => {
    const total = info.order.originalPrice + (info.order.deposit ?? 0)
    return total
  }

  return (
    <>
      <Flex justifyContent="center" alignItems="center" flexDirection="column" gridGap="12px" marginBottom="24px">
        <Flex justifyContent="space-between" flexDirection="row" gridGap="12px" width="100%">
          <Box>
            <Text fontSize="smd" fontWeight={300}>
              {t('common:totalPrice')}
            </Text>
            <Text fontSize="2xl">
              {removeDecimals(getTotalPrice())} {t('common:nok')}
            </Text>
            {info.order.deposit !== undefined && info.order.deposit > 0 && (
              <Text fontSize="xs" fontWeight={300}>
                {t('common:deposit')}: {removeDecimals(info.order.deposit)} {t('nok')} ({t('included')})
              </Text>
            )}
            {(info.order.discountCodeId !== undefined ||
              info.order.refund !== undefined ||
              (info.order.credits !== undefined && info.order.credits > 0) ||
              (info.order.refundCredits !== undefined && info.order.refundCredits > 0)) && (
              <>
                <Text fontSize="lg" fontWeight={300} textDecoration="line-through">
                  {removeDecimals(getOriginalPrice())} {t('common:nok')}
                </Text>
                <Text fontSize="xs" fontWeight={300}>
                  {getReductionDescriptionText(info.order, t)}
                </Text>
              </>
            )}

            <Text fontSize="xs" fontWeight={300}>
              {t('common:priceIncludesVAT')}
            </Text>
            <Text fontSize="xs" fontWeight={300}>
              {`${t('common:order')} ${info.order.id}`}
            </Text>
          </Box>
        </Flex>

        <PayForReconciliationsButtons reconciliations={info.reconciliations} />
        <ContinuePaymentButtons orderInfo={info} refetchOrder={refetch} />
      </Flex>

      <Flex justifyContent="center" alignItems="center" direction="column" gridGap="24px">
        {orderItems.map((orderItem) => (
          <IndividualOrderItemInfo
            key={orderItem.orderItem.id}
            orderItemInfo={orderItem}
            orderItems={orderItems}
            order={info.order}
            refetchOrder={refetch}
            orderIsLoading={orderIsLoading}
            isPublicPage={isPublicPage}
          />
        ))}
      </Flex>
    </>
  )
}

export default IndividualOrderInfo
